import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { addMoney, Banner, Button, Card, compareMoney, Dialog, Field, formatMoney, isZero, Money, negate, PageHeader, Pill } from '@elysian/ui';
import type { CashDocument, DocumentSummary } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso, isAmount, longDate, shortDate } from '../invoices/shared';

type Pending = null | 'change' | 'refund' | 'undo';

const what = (c: CashDocument) => (c.is_refund ? 'refund' : c.type === 'receipt' ? 'receipt' : 'payment');
const docPath = (type: string | undefined, id: string) =>
  type === 'sales_invoice' ? `/invoices/${id}` : type === 'purchase_bill' ? `/bills/${id}` : `/receipts/${id}`;

/**
 * One receipt, payment or refund (#26): what it pays, what's held on account, whether
 * it's in the bank yet, and the ways to put it right: change what it pays, refund the
 * credit, or undo it if it was entered by mistake (db/038).
 */
export function ReceiptPage() {
  const { id = '' } = useParams();
  const { source, can, today } = useSession();
  const toast = useToast((s) => s.show);
  const [version, setVersion] = useState(0);
  const [pending, setPending] = useState<Pending>(null);
  const data = useAsync(() => source.cashDocument(id), [source, id, version], { keep: true });

  if (data.state === 'loading') return <PageHeader title="Receipt" subtitle="Loading…" />;
  if (data.state === 'error') return <><PageHeader title="Receipt" /><Banner>Couldn’t open it: {data.error.message}</Banner></>;
  const c = data.data;
  const kind = what(c);
  const isCustomer = c.is_refund ? c.type === 'payment' : c.type === 'receipt';
  const contactPath = `/${isCustomer ? 'customers' : 'suppliers'}/${c.contact_id}`;
  const live = c.status !== 'void';
  const mayWrite = can('documents.write') && live;
  const pays = c.allocations.filter((a) => a.type !== 'payment' && a.type !== 'receipt');
  const refunds = c.allocations.filter((a) => a.type === 'payment' || a.type === 'receipt');
  const held = !c.is_refund && live && compareMoney(c.on_account, '0') > 0;
  const title = c.is_refund ? `Refund ${c.number}` : `${kind === 'receipt' ? 'Receipt' : 'Payment'} ${c.number}`;
  const done = (msg: string) => { setPending(null); toast(msg); setVersion((v) => v + 1); };

  return (
    <>
      <div style={{ marginBottom: 6 }}><Link to={contactPath} className="el-hint">← {c.contact_name}</Link></div>
      <PageHeader title={title}
        subtitle={<>{c.is_refund ? 'Paid back to' : kind === 'receipt' ? 'From' : 'To'} <Link to={contactPath}>{c.contact_name}</Link> · <Money value={c.amount} currency={c.currency} /> · {shortDate(c.date)}</>}
        actions={mayWrite && (
          <>
            {!c.is_refund && <Button onClick={() => setPending('change')}>Change what it pays</Button>}
            {held && <Button onClick={() => setPending('refund')}>{kind === 'receipt' ? 'Refund the credit' : 'Refund from the supplier'}</Button>}
            <Button variant="danger" onClick={() => setPending('undo')}>Undo this {kind}</Button>
          </>
        )} />
      <div className="el-row" style={{ marginBottom: 16 }}>
        {!live ? <Pill tone="draft">Undone</Pill> : c.in_bank ? <Pill tone="ok">In the bank</Pill> : <Pill tone="info">Not matched to the bank yet</Pill>}
        {held && <Pill tone="info">{formatMoney(c.on_account, c.currency)} on account</Pill>}
      </div>
      {!live && c.voided && (
        <div style={{ marginBottom: 16 }}><Banner>Undone on {longDate(c.voided.date ?? c.date)}. {c.voided.reason}</Banner></div>
      )}

      <div className="el-two-col">
        <Card as="section" padding="18px 20px" aria-labelledby="rc-details">
          <h2 id="rc-details" className="el-h2" style={{ marginBottom: 12 }}>Details</h2>
          <dl className="el-kv">
            <dt>Date</dt><dd>{longDate(c.date)}</dd>
            <dt>Amount</dt><dd><Money value={c.amount} currency={c.currency} /></dd>
            <dt>{c.is_refund ? 'Paid from' : kind === 'receipt' ? 'Paid into' : 'Paid from'}</dt><dd>{c.bank_account?.name ?? '—'}</dd>
            <dt>Reference</dt><dd>{c.reference ?? '—'}</dd>
            {c.is_refund && <><dt>Refunds</dt><dd>{c.refunds ?? '—'}</dd></>}
            {c.notes && !c.is_refund && <><dt>Notes</dt><dd>{c.notes}</dd></>}
          </dl>
        </Card>
        {!c.is_refund && (
          <Card as="section" padding="18px 0 8px" aria-labelledby="rc-pays">
            <div className="el-card-head"><h2 id="rc-pays" className="el-h2">What it pays</h2></div>
            <div className="el-table-wrap">
              <table className="el-table" aria-label="What it pays">
                <tbody>
                  {pays.map((a) => (
                    <tr key={a.document_id}>
                      <td><Link className="el-row-link" to={docPath(a.type, a.document_id)}>{a.number}</Link></td>
                      <td className="el-num"><Money value={a.amount} currency={c.currency} /></td>
                    </tr>
                  ))}
                  {refunds.map((a) => (
                    <tr key={a.document_id}>
                      <td>Refunded by <Link className="el-row-link" to={`/receipts/${a.document_id}`}>{a.number}</Link></td>
                      <td className="el-num"><Money value={a.amount} currency={c.currency} /></td>
                    </tr>
                  ))}
                  {live && <tr><td className="el-muted">Held on account</td><td className="el-num"><Money value={c.on_account} currency={c.currency} /></td></tr>}
                  {!live && <tr><td className="el-muted" colSpan={2}>Nothing: it was undone, so the {c.type === 'payment' ? 'bills' : 'invoices'} it paid are owed again.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {pending === 'change' && <ChangeAllocations cash={c} onClose={() => setPending(null)} onDone={done} />}
      {pending === 'refund' && <Refund cash={c} now={iso(today())} onClose={() => setPending(null)} onDone={done} />}
      {pending === 'undo' && <Undo cash={c} now={iso(today())} onClose={() => setPending(null)} onDone={done} />}
    </>
  );
}

const cents = (v: string) => Math.round(Number(v) * 100);

/** Change what a receipt pays: its invoices and the customer's other unpaid ones, prefilled with what it pays now. */
export function ChangeAllocations({ cash: c, onClose, onDone }: { cash: CashDocument; onClose: () => void; onDone: (msg: string) => void }) {
  const { source } = useSession();
  const current = useMemo(() => new Map(c.allocations.filter((a) => a.type !== 'payment' && a.type !== 'receipt').map((a) => [a.document_id, a.amount])), [c]);
  const docs = useAsync(async () => (await source.documents({ type: c.type === 'receipt' ? 'sales_invoice' : 'purchase_bill', contactId: c.contact_id }))
    .filter((d) => current.has(d.id) || ((d.status === 'posted' || d.status === 'part_paid') && Number(d.outstanding) > 0 && d.currency === c.currency))
    .sort((a, b) => a.issue_date.localeCompare(b.issue_date)), [source, c.contact_id, current]);
  const [amounts, setAmounts] = useState<Record<string, string>>(() =>
    Object.fromEntries([...current].map(([k, v]) => [k, Number(v).toFixed(2)])));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list: DocumentSummary[] = docs.state === 'ready' ? docs.data : [];
  const refunded = addMoney('0', ...c.allocations.filter((a) => a.type === 'payment' || a.type === 'receipt').map((a) => a.amount));
  const available = cents(addMoney(c.amount, negate(refunded)));
  const total = Object.values(amounts).reduce((t, v) => t + (v.trim() && isAmount(v) ? cents(v) : 0), 0);
  const maxFor = (d: DocumentSummary) => cents(d.outstanding) + cents(current.get(d.id) ?? '0');
  const bad = Object.entries(amounts).find(([, v]) => v.trim() && !isAmount(v));
  const tooMuch = list.find((d) => amounts[d.id]?.trim() && isAmount(amounts[d.id]!) && cents(amounts[d.id]!) > maxFor(d));
  const reduced = [...current].some(([k, v]) => cents(amounts[k] || '0') < cents(v));
  const unchanged = [...new Set([...current.keys(), ...Object.keys(amounts)])].every((k) => cents(amounts[k] || '0') === cents(current.get(k) ?? '0'));
  const problem = bad ? 'An amount can’t be read' : tooMuch ? `${tooMuch.doc_no} has only ${formatMoney((maxFor(tooMuch) / 100).toFixed(2), c.currency)} to pay`
    : total > available ? `That’s ${formatMoney(((total - available) / 100).toFixed(2), c.currency)} more than the ${c.type} has` : null;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const allocations = Object.entries(amounts).filter(([, v]) => v.trim() && Number(v) > 0).map(([document_id, v]) => ({ document_id, amount: Number(v).toFixed(2) }));
      const r = await source.reallocateCash(c.id, allocations, reduced ? reason.trim() : undefined);
      onDone(`${c.number} now pays ${r.allocations.filter((a) => a.type !== 'payment' && a.type !== 'receipt').map((a) => a.number).join(', ') || 'nothing'}`
        + (compareMoney(r.on_account, '0') > 0 ? `; ${formatMoney(r.on_account, c.currency)} on account` : ''));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title={`Change what ${c.number} pays`} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy || unchanged || !!problem || (reduced && reason.trim().length < 3)} onClick={() => void save()}>Save</Button></>}>
      <p style={{ margin: 0 }}>{formatMoney(c.amount, c.currency)} {c.type === 'payment' ? 'paid' : 'received'} on {shortDate(c.date)}. Anything not used against {c.type === 'payment' ? 'bills' : 'invoices'} is held on account as credit.</p>
      {docs.state === 'loading' && <div className="el-skeleton" style={{ height: 60 }} />}
      {list.length === 0 && docs.state === 'ready' && <p className="el-muted" style={{ margin: 0 }}>{c.contact_name} has no unpaid {c.type === 'payment' ? 'bills' : 'invoices'}.</p>}
      {list.length > 0 && (
        <div className="el-table-wrap">
          <table className="el-table" aria-label={c.type === 'payment' ? 'Bills it pays' : 'Invoices it pays'}>
            <thead><tr><th scope="col">{c.type === 'payment' ? 'Bill' : 'Invoice'}</th><th scope="col" className="el-hide-narrow">Dated</th><th scope="col" className="el-num">To pay</th><th scope="col" className="el-num">Pays</th></tr></thead>
            <tbody>
              {list.map((d) => (
                <tr key={d.id}>
                  <td>{d.doc_no}</td>
                  <td className="el-hide-narrow">{shortDate(d.issue_date)}</td>
                  <td className="el-num"><Money value={(maxFor(d) / 100).toFixed(2)} currency={d.currency} /></td>
                  <td className="el-num" style={{ width: 120, minWidth: 100 }}>
                    <input className="el-input el-num" inputMode="decimal" aria-label={`Pays ${d.doc_no}`} value={amounts[d.id] ?? ''}
                      onChange={(e) => setAmounts({ ...amounts, [d.id]: e.target.value })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="el-receipt-sum" aria-live="polite">
        {formatMoney((total / 100).toFixed(2), c.currency)} against {c.type === 'payment' ? 'bills' : 'invoices'} · <strong>{formatMoney((Math.max(available - total, 0) / 100).toFixed(2), c.currency)} on account</strong>
      </p>
      {reduced && (
        <Field id="ca-reason" label="Why it’s coming off" hint="Kept with the change in the books">
          <input id="ca-reason" className="el-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Allocated to the wrong invoice" />
        </Field>
      )}
      {problem && <div className="el-error-box" role="alert">{problem}</div>}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}

/** Pay credit held on account back to the customer, out of a chosen bank account. */
export function Refund({ cash: c, now, onClose, onDone }: { cash: CashDocument; now: string; onClose: () => void; onDone: (msg: string) => void }) {
  const { source } = useSession();
  const banks = useAsync(async () => (await source.bankAccounts()).filter((b) => b.is_active !== false && b.currency === c.currency), [source, c.currency]);
  const [amount, setAmount] = useState(Number(c.on_account).toFixed(2));
  const [date, setDate] = useState(now < c.date ? c.date : now);
  const [bank, setBank] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = banks.state === 'ready' ? banks.data : [];
  const chosen = bank ?? c.bank_account?.code ?? list[0]?.code ?? '';
  const toUs = c.type === 'payment'; // a supplier paying credit back to us
  const ok = isAmount(amount) && Number(amount) > 0 && compareMoney(amount, c.on_account) <= 0;

  async function go() {
    setBusy(true);
    setError(null);
    try {
      const r = await source.refundOnAccount(c.id, { date, amount: Number(amount).toFixed(2), bank_account: chosen, reference: reference.trim() || null });
      onDone(toUs ? `${r.number}: ${formatMoney(r.amount, c.currency)} refunded by ${c.contact_name} into ${r.bank_account?.name ?? 'the bank'}`
        : `${r.number}: ${formatMoney(r.amount, c.currency)} refunded to ${c.contact_name} from ${r.bank_account?.name ?? 'the bank'}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title={toUs ? `Refund from ${c.contact_name}` : `Refund credit to ${c.contact_name}`} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy || !ok || !chosen || !date || date < c.date} onClick={() => void go()}>Record the refund</Button></>}>
      <p style={{ margin: 0 }}>{formatMoney(c.on_account, c.currency)} of {c.number} is held on account. Record the money {toUs ? `${c.contact_name} paid back to you` : 'you’ve paid back'}; the bank reconciliation will offer it for the matching statement line.</p>
      <div className="el-form-grid">
        <Field id="rf-amount" label="Amount refunded" error={amount && !isAmount(amount) ? 'An amount, such as 50.00' : compareMoney(amount || '0', c.on_account) > 0 ? 'More than is on account' : null}>
          <input id="rf-amount" className="el-input el-num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field id="rf-date" label={toUs ? 'Date received' : 'Date paid'}><input id="rf-date" type="date" className="el-input" min={c.date} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field id="rf-bank" label={toUs ? 'Paid into' : 'Paid from'} hint={toUs ? 'The bank account it came into' : 'The bank account it went out of'}>
          <select id="rf-bank" className="el-input" value={chosen} onChange={(e) => setBank(e.target.value)}>
            {list.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
        </Field>
        <Field id="rf-ref" label="Reference (optional)"><input id="rf-ref" className="el-input" value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
      </div>
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}

/** Undo a receipt, payment or refund entered by mistake. */
export function Undo({ cash: c, now, onClose, onDone }: { cash: CashDocument; now: string; onClose: () => void; onDone: (msg: string) => void }) {
  const { source } = useSession();
  const kind = what(c);
  const [date, setDate] = useState(now < c.date ? c.date : now);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pays = c.allocations.filter((a) => a.type !== 'payment' && a.type !== 'receipt');

  async function go() {
    setBusy(true);
    setError(null);
    try {
      await source.undoCash(c.id, date, reason.trim());
      onDone(`${c.number} undone` + (pays.length ? `; ${pays.map((a) => a.number).join(', ')} owed again` : ''));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title={`Undo ${c.number}?`} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Keep it</Button>
        <Button variant="danger" disabled={busy || c.in_bank || reason.trim().length < 3 || !date || date < c.date} onClick={() => void go()}>Undo {kind}</Button></>}>
      {c.in_bank
        ? <Banner>{c.number} is matched to a bank statement line. Unmatch it on the <Link to="/bank">bank reconciliation</Link> first, then undo it.</Banner>
        : <p style={{ margin: 0 }}>
            For a {kind} entered by mistake. Nothing is deleted: the {kind} is reversed and marked undone
            {pays.length ? <>, and {pays.map((a) => a.number).join(', ')} {pays.length === 1 ? 'is' : 'are'} owed again</> : null}
            {c.is_refund ? <>, and the credit goes back on {c.contact_name}’s account</> : null}.
          </p>}
      <Field id="un-date" label="Date"><input id="un-date" type="date" className="el-input" min={c.date} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      <Field id="un-reason" label="Reason" hint="Kept in the books and the audit trail">
        <input id="un-reason" className="el-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Entered twice" />
      </Field>
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}

export const isRefundable = (c: CashDocument) => c.status !== 'void' && !c.is_refund && !isZero(c.on_account);
